import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormDisplayService } from './form-display.service';
import { FormsExtendedDataApiModel, ApplicationDTO, QuestionGroup, Question } from '../../forms/forms.model';
import { QuestionType } from 'src/AngularJS/Models/Quality/questions.models';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';

@Component({
  selector: 'cv-form-display',
  templateUrl: './form-display.component.html',
  styleUrls: ['./form-display.component.scss']
})
export class FormDisplayComponent implements OnInit {
    @Input() applicationId: number = 0; // ApplicationId to load and display
    @Input() recordingId: number = 0; // RecordingId to load and save against
    @Input() viewOnly: boolean = false; // Use this flag to determine enable/disabled state for view
    @Input() isCalibration: boolean = false; // Use this flag to determine if save is a regular form or a calibration
    @Input() includeDelete: boolean = false; // Use this flag to determine if the delete button should be shown
    @Input() previewOnly: boolean = false; // Use this flag to remove save and cancel button
    @Input() displayScore: boolean = false; // Use this flag to determine if the score should be displayed
    @Input() score?:number = null; // If the displayScore is true, this will either be the score or N/A
    @Output() buttonClick = new EventEmitter<boolean>();

    public isLoading: boolean = false;
    public isNew: boolean = true;
    public applicationDto: ApplicationDTO = null;
    public formDataToDisplay: FormsExtendedDataApiModel = null;
    public origAnswerMap: Map<number, string> = new Map(); // Map of the original loaded answer to be used to determine changes and during cancel actions
    public origListAnswerMap: Map<number, Array<string>> = new Map(); // Map of the original loaded answer to be used to determine changes and during cancel actions
    public answerMap: Map<number, string> = new Map();  // Map to hold the selected answers Key:QuestionID Value:AnswerID or AnswerText
    public listAnswerMap: Map<number, Array<string>> = new Map(); //Map of Key: Question ID Value: List of AnswerID or AnswerText

    public localization: Map<string, string>;

    constructor(
        public formDisplayService: FormDisplayService,
        private cvLocalizationFactory: CVLocalizationService,
    ) {
    }

    async ngOnInit() {
        this.isLoading = true;
        this.cvLocalizationFactory.GetSection("Forms").then((returnObj) => {
            this.localization = returnObj;
        }).catch((error) => {
            console.log(error);
        });

        if (this.applicationId <= 0)
            return;

        this.applicationDto = await this.formDisplayService.loadFormDto(this.applicationId);
        // This should load most/all the data needed for the display/create mode. Does not handle the selectedAnswers part
        this.formDataToDisplay = await this.formDisplayService.loadFormExtendedData(this.applicationId);

        let returnedMap: Map<number, Array<string>> = new Map();
        if (this.recordingId > 0) {
            let type: number = this.isCalibration ? 1 : 0;
            // Try to pull any previously saved answers
            returnedMap = await this.formDisplayService.loadSelectedAnswers(this.applicationId, this.recordingId, type);
        }

        if (returnedMap.size > 0)
            this.isNew = false;

        // Loop over returned form questions and set the answers to either an intial state or the retuned value.
        // This will drop answers returned that were attached to deleted questions and initialize new questions without returned answers.
        for (let questionGroup of this.formDataToDisplay.QuestionGroups) {
            for (let question of questionGroup.Questions) {
                if (returnedMap.has(question.QuestionID))
                    this.listAnswerMap.set(question.QuestionID, returnedMap.get(question.QuestionID));
                else if (question.QuestionType == 3) // Text type question
                    this.listAnswerMap.set(question.QuestionID, [""]); // Initialize text fields to empty string
                else if (question.QuestionType == 4) // Checkbox
                    this.listAnswerMap.set(question.QuestionID, [question.Answers[1].AnswerID.toString()]); // Initialize the checkbox with the unchecked answerID
                else
                    this.listAnswerMap.set(question.QuestionID, ["0"]); // Initialize selectedAnswer fields to id:0
            }
        }

        for (let [key, value] of this.listAnswerMap) {
            this.origListAnswerMap.set(key, [...value]);
        }

        this.isLoading = false;
    }

    public updateCheckbox(questionId, checkAnswer, uncheckAnswer) {
        if (this.listAnswerMap.get(questionId)[0] != checkAnswer + "") // If not checkAnswer then set to checkAnswer
            this.listAnswerMap.set(questionId, [checkAnswer + ""]);
        else
            this.listAnswerMap.set(questionId, [uncheckAnswer + ""]);
    }

    public updateMultiSelectAnswer(questionId, answerId) {
        if (this.listAnswerMap.get(questionId).includes(answerId)) {
            let index: number = this.listAnswerMap.get(questionId).indexOf(answerId);
            let newList: Array<string> = [...this.listAnswerMap.get(questionId)];
            newList.splice(index, 1);

            if (newList.length == 0)
                newList.push("0");
            this.listAnswerMap.set(questionId, newList);
        } else {
            let newList: Array<string> = [...this.listAnswerMap.get(questionId)];
            if (newList.length >= 1 && newList.includes("0")) {
                let index: number = newList.indexOf(answerId);
                newList.splice(index, 1);
            }
                
            newList.push(answerId);
            this.listAnswerMap.set(questionId, newList);
        }
    }

    public checkDependency(question: Question, qg: QuestionGroup) {
        if (question.DependentID == 0)
            return true; // Not dependent so show

        if(!qg.Questions.find(q => q.QuestionID == question.DependentID))
            return false; //Avoid undefined error for the next validation

        if (this.listAnswerMap.get(qg.Questions.find(q => q.QuestionID == question.DependentID).QuestionID).includes("") || this.listAnswerMap.get(qg.Questions.find(q => q.QuestionID == question.DependentID).QuestionID).includes("0")) {
            if (question.QuestionType == 3) // Remove any selected answer from the dependent question when it gets hidden
                this.listAnswerMap.set(question.QuestionID, [""]);
            else if (question.QuestionType == 4) // Checkbox
                this.listAnswerMap.set(question.QuestionID, [question.Answers[1].AnswerID.toString()]); // Update the checkbox with the unchecked answerID
            else
                this.listAnswerMap.set(question.QuestionID, ["0"]);

            return false; // Dependent question and parent question was not found or is not answered
        }

        if (qg.Questions.find(q => q.QuestionID == question.DependentID).Answers.find(a => this.listAnswerMap.get(qg.Questions.find(q => q.QuestionID == question.DependentID).QuestionID).includes(a.AnswerID + "")).ShowsDependents)
            return true; // Dependent question and parent answer has showDependents

        if (question.QuestionType == 3) // Remove any selected answer from the dependent question when it gets hidden
            this.listAnswerMap.set(question.QuestionID, [""]);
        else if (question.QuestionType == 4) // Checkbox
            this.listAnswerMap.set(question.QuestionID, [question.Answers[1].AnswerID.toString()]); // Update the checkbox with the unchecked answerID
        else
            this.listAnswerMap.set(question.QuestionID, ["0"]);

        return false; // Parent answer does not have show dependents then hide
    }

    public cancelClick() {
        this.listAnswerMap.clear();
        for (let [key, value] of this.origListAnswerMap) {
            this.listAnswerMap.set(key, [...value]);
        }

        this.buttonClick.emit(false);
    }

    public async saveClick() {
        // Add actual save part here
        if (this.validateAnswers()) {
            await this.formDisplayService.saveForm(this.recordingId, this.applicationId, this.isCalibration, this.listAnswerMap);
            this.origListAnswerMap.clear();
            for (let [key, value] of this.listAnswerMap) { //once save action is complete then set the original value to the new saved set.
                this.origListAnswerMap.set(key, [...value]);
            }
            this.buttonClick.emit(true);
        }
        else
            showErrorMessage(this.localization.get("Questions_Unanswered"));
    }

    public disableSave() {
        if (this.listAnswerMap.size == 0 || this.origListAnswerMap.size == 0)
            return true; // Loading has not finished yet and when it does they will match

        for (let [key, value] of this.listAnswerMap) {
            if (value.toString() != this.origListAnswerMap.get(key).toString())
                return false; // Current answer does not match original answer so save can happen
        }

        return true;
    }

    public validateAnswers() {
        for (let [key, value] of this.listAnswerMap) {
            // "0" is the default value when no answer is selected, so if it isn't that, then it passes validation
            if (!value.includes("0"))
                continue;

            // Only allow values of "0" if it is a hidden dependent question or a text type that happens to have this value
            for (let questionGroup of this.formDataToDisplay.QuestionGroups) {
                let tempQuestion = questionGroup.Questions.find(q => q.QuestionID == key);

                if (tempQuestion != null) { // Question missing answer found
                    // Text questions pass validation in this case
                    if (tempQuestion.QuestionType == QuestionType.Text)
                        break;

                    if (this.checkDependency(tempQuestion, questionGroup))
                        return false; // Question is not valid due to missing a selected answer and being determined to be shown
                    else
                        break; // Question is a hidden dependent
                }
            }
        }

        // All questions passed validation
        return true;
    }

    public async deleteClick() {
        if (!this.viewOnly && !this.isNew && this.includeDelete) {
            await this.formDisplayService.removeMonitoredRecording(this.recordingId, this.applicationId, this.isCalibration);
            this.origListAnswerMap.clear();
            this.buttonClick.emit(true);
        }
        else
            showErrorMessage(this.localization.get("Delete_Unavailable"));
    }
}
